/**
 * 多版本菜单：地址里跟着走的三个键 —— 菜单预览码 `previewMenu`（B6 U-B6-3）、点单链接码 `menuLink`（N35-B5）、进店参数 `mmEntry`（N35-B2 第 4 轮）。
 * 三个都只活在地址里（零存储）：站内跳转靠路由守卫从上一页补到下一页，整页跳转靠 `withMenuAddress` 顺手带上。
 *
 * `router/index.js` 的第一个 beforeEach（排在组件预加载守卫与老守卫之前：老守卫的重定向只拼 to.query，先补进 to，它们再原样带走）：
 *   `const next = menuAddressFor(to, from, { pageLoad: !firstNavigationLanded, state: store.state })`
 *   「整页加载」= 这一页的第一个导航还没落地（router/index.js 里第一个 afterEach 置位）。不用 `VueRouter.START_LOCATION`：
 *   它是 vue-router 3.5.0 才有的，服务器构建实际装的是 3.3.4（N35-B2 第 5 轮；兼容测试 `npm run test:router-compat`）。
 *
 * 读的是上一页的地址与 store 里「这是哪家店」（url_id ⇄ UUID），不写任何存储。
 */
import { carryPreviewMenu, PREVIEW_MENU_QUERY_KEY } from './previewMenu'
import { carryMenuLink, currentMenuLinkCode, MENU_LINK_QUERY_KEY } from './menuLink'
import { ENTRY_QUERY_KEY, carryEntryValue, firstEntryValue, landingEntryLocation } from './qrEntry'
import { isSpRoute } from './qrMenu'
import { NO_INPUT_TABLE_NUMBER } from '@/utils/constants'

/**
 * 路由守卫：返回要改写成的地址（`next(location)`），不用改写 ⇒ null。补过之后再判一次一定是 null（不死循环）。
 * - 整页加载的第一个导航（`pageLoad`）：上一页是 START（没有参数可补），只做进店那一刻的「写进地址 / Custom Flows 去掉」（`qrEntry.landingEntryLocation`，replace）。
 * - 站内跳转：三个键各自补（下一页自己带着合法的就用它的）——
 *   - `previewMenu`：照 B6（`carryPreviewMenu`）；**只有它要补时返回值与 B6 逐字节相同**；
 *   - `menuLink`：同一口径（`carryMenuLink`；合单不带由读的那一头 `menuEntry` 管）；
 *   - `mmEntry`：只在同一家店内补、不进 Custom Flows（`qrEntry.carryEntryValue`）；下一页是 Custom Flows 却带着它 ⇒ 去掉（Q19）。
 * @param {Object} to
 * @param {Object} from
 * @param {Object} [opts]
 * @param {Boolean} [opts.pageLoad] 这一页的第一个导航还没落地（from 是 vue-router 的起点 START；重定向的第二遍也算）
 * @param {Object} [opts.state] root state（门店 UUID 与 url_id）
 * @returns {Object|null}
 */
export function menuAddressFor(to, from, { pageLoad = false, state } = {}) {
  if (pageLoad) return landingEntryLocation(to)
  const preview = carryPreviewMenu(to, from)
  const link = carryMenuLink(to, from)
  const entry = carryEntryValue(to, from, state)
  const query = (to && to.query) || {}
  const strip = isSpRoute(to) && Object.prototype.hasOwnProperty.call(query, ENTRY_QUERY_KEY)
  if (!link && entry === null && !strip) return preview
  const next = { ...query }
  if (preview) next[PREVIEW_MENU_QUERY_KEY] = preview.query[PREVIEW_MENU_QUERY_KEY]
  if (link) next[MENU_LINK_QUERY_KEY] = link.query[MENU_LINK_QUERY_KEY]
  if (entry !== null) next[ENTRY_QUERY_KEY] = entry
  if (strip) delete next[ENTRY_QUERY_KEY]
  return { path: to.path, query: next, hash: to.hash || '' }
}

/**
 * 整页跳转（`window.location.href = …`）的目标地址补上当前地址里的进店参数与点单链接码（card-detail 3DS 后跳订单页 / 付款页 /
 * 挂账页那几处）。当前是 Custom Flows 页 ⇒ 不带进店参数（Q19）；目标是合单 `/vgs/` ⇒ 原样；目标自己已经有 ⇒ 不重复；
 * 当前地址没有 ⇒ 原样（逐字节）。
 * @param {String} href 目标地址（绝对或相对）
 * @param {Object} [route] 当前路由（`this.$route`）
 * @returns {String}
 */
export function withMenuAddress(href, route) {
  if (typeof href !== 'string') return href
  const query = (route && route.query) || {}
  const entry = isSpRoute(route) ? null : firstEntryValue(query[ENTRY_QUERY_KEY])
  const link = currentMenuLinkCode(query)
  if (!entry && !link) return href
  const hashAt = href.indexOf('#')
  const base = hashAt < 0 ? href : href.slice(0, hashAt)
  const hash = hashAt < 0 ? '' : href.slice(hashAt)
  const queryAt = base.indexOf('?')
  const path = queryAt < 0 ? base : base.slice(0, queryAt)
  if (/\/vgs\//.test(path.replace(/^https?:\/\/[^/]+/, ''))) return href
  let existing
  try {
    existing = new URLSearchParams(queryAt < 0 ? '' : base.slice(queryAt + 1))
  } catch (e) {
    return href
  }
  const extra = []
  if (entry && !existing.has(ENTRY_QUERY_KEY)) extra.push(`${ENTRY_QUERY_KEY}=${entry}`)
  if (link && !existing.has(MENU_LINK_QUERY_KEY)) extra.push(`${MENU_LINK_QUERY_KEY}=${link}`)
  if (!extra.length) return href
  return base + (queryAt < 0 ? '?' : '&') + extra.join('&') + hash
}

/**
 * 站内浏览器（Instagram / Facebook / 微信）弹窗里「COPY MENU」复制的地址（App.vue `getVenueUrl`，review 第 4 轮 R4B5-3）：
 * 顾客把它粘到 Safari / Chrome 里整页打开，所以一律带 `qr=1`（照扫码统计，与改动前相同）。
 * 按地址上的进店参数拼，**不按此刻的路径猜**（Q16 / Q17：原来按首页此刻的路径重拼，柜台 / 通用桌码顾客填过桌号 7 后复制到的是单桌码
 * `/t/7?qr=1`，打开就成了「扫了 7 号桌」；进店参数和点单链接码也都丢）：
 * - `table`：路径里的桌号（就是扫的那张桌）；
 * - `gtable`：通用桌码 `/t/n-t`（顾客自己填的桌号不拼进去）；
 * - `counter`：`/p` 加上地址上原有的 `portal=true`（自己填的桌号同样不拼）；
 * - 没有进店参数（`/p`、`/t` 进店、书签、Custom Flows …）：照旧 —— 路径里有桌号拼 `/t/<桌>`，否则 `/p`（桌名是字母数字时与改动前逐字节相同）；
 * 再用 `withMenuAddress` 补上 `mmEntry` 与 `menuLink`（Custom Flows 页只补码）。
 * 桌名先取准（N35-B2 第 5 轮，`pathTableName`）：原来对 `route.path` 里那一段直接 `encodeURI`，而路由的 path 有时已经是转义过的
 * （具名重定向来的 —— 扫单桌码 `/t/Table%201` 落到首页就是这样，两个版本都是；vue-router 3.6.5 整页加载也是），于是复制成 `Table%25201`，
 * 打开是另一张桌；只有 3.3.4 整页加载（路径先被 decodeURI）时碰巧对。商品页 `/t/<桌>/<商品>` 还会把商品 id 拼进桌号。
 * 再按当前路由器读地址栏的方式转义（第 5 轮复核，`tablePathSegment`）：桌名里没有 `%` 转义一次（两版同一个地址）；有 `%` 时看路由器。
 * @param {String} base `<站点>[/online]/v/<url_id 或 UUID>`
 * @param {Object} [route] 当前路由（`this.$route`）
 * @param {Object} [router] 当前路由器（`this.$router`）：只在桌名里有 `%` 时用来看它读地址栏时先不先解码；不给 ⇒ 当它不先解码（转义一次）
 * @returns {String}
 */
export function menuCopyUrl(base, route, router) {
  const path = route && typeof route.path === 'string' ? route.path : ''
  const query = (route && route.query) || {}
  const entry = isSpRoute(route) ? null : firstEntryValue(query[ENTRY_QUERY_KEY])
  let tail
  if (entry === 'gtable') {
    tail = `/t/${NO_INPUT_TABLE_NUMBER}?qr=1`
  } else if (entry === 'counter') {
    tail = '/p?qr=1' + (query.portal === 'true' ? '&portal=true' : '')
  } else {
    const table = pathTableName(path)
    tail = (table ? `/t/${tablePathSegment(table, router)}` : '/p') + '?qr=1'
  }
  return withMenuAddress(base + tail, route)
}

/**
 * 桌名写进复制地址路径的那一段（N35-B2 第 5 轮复核，Codex P1）。
 * - 桌名里没有 `%`：转义一次 —— 两个版本整页打开读出来都是它（3.3.4 先 decodeURI 的那一下只解出非保留字符，再 decodeURIComponent 一次照样是它）。
 * - 有 `%`（`50%`、长得像转义的 `A%20B`）：没有一种写法两版都对。3.3.4 整页打开时路径先 decodeURI 一次、匹配时再 decodeURIComponent 一次，
 *   只转义一次的 `50%25` 在那里直接抛 URI malformed（页面起不来，守卫都还没跑）、`A%2520B` 读成 `A B`；转义两次在 3.6.5 上又多一层（`50%25`）。
 *   ⇒ 看当前这台路由器（`routerDecodesAddressPath`，按行为看）：先解码的转义两次，否则一次。复制出去的地址在同一个站上打开、跑的是同一版路由器。
 *   残留：服务器换路由器版本之前复制、之后才打开的、桌名带 `%` 的地址会读错（只这一种，已登记）。
 * @param {String} table 桌名（没转义的）
 * @param {Object} [router]
 * @returns {String}
 */
function tablePathSegment(table, router) {
  const once = encodeURIComponent(table)
  return table.includes('%') && routerDecodesAddressPath(router) ? encodeURIComponent(once) : once
}

/**
 * 这台路由器读地址栏时是不是先把路径解码一次 —— vue-router 3.3.4 的 `history.getCurrentLocation()` 是 `decodeURI(pathname)` + search + hash，
 * 3.6.5 原样。按行为看（读一次、与浏览器地址栏的原样比），不按版本号、也不靠 3.5 起才有的导出（N35-B2 第 5 轮的教训）。
 * - 地址栏路径里没有能被 decodeURI 解开的转义时两种读法一样 ⇒ false（看不出，也用不着：COPY MENU 只在桌名里有 `%` 时问，那时路径里一定有 `%25`）；
 * - 读地址栏抛错 ⇒ true（只有先解码的那种会抛：路径里有解不开的 `%`）；
 * - 没有路由器、不是 history 模式 ⇒ false。
 * @param {Object} [router] `this.$router`
 * @returns {Boolean}
 */
export function routerDecodesAddressPath(router) {
  const history = router && router.history
  if (!router || router.mode !== 'history' || !history || typeof history.getCurrentLocation !== 'function') return false
  if (typeof window === 'undefined' || !window.location) return false
  const { pathname, search, hash } = window.location
  const base = typeof history.base === 'string' ? history.base : ''
  const path = base && pathname.toLowerCase().indexOf(base.toLowerCase()) === 0 ? pathname.slice(base.length) : pathname
  let read
  try {
    read = history.getCurrentLocation()
  } catch (e) {
    return true
  }
  return read !== (path || '/') + search + hash
}

/**
 * 路由路径里的桌名（没转义的原样），没有 ⇒ ''：取 `/t/` 后面那一段（到下一个 `/` 为止 —— 商品页 `/t/<桌>/<商品>` 不带商品 id），解码一次
 * （解不开 —— 3.3.4 已经解码过、桌名里有裸 `%` —— 就原样）。与 vue-router 版本无关：那一段是转义的（具名重定向落地、3.6.5）还是解码过的
 * （3.3.4 整页加载 / 后退）都得到同一个桌名，与路由参数 `number` 一致；ssp 店的静态首页 `/v/ssp_new_york_burger/home/t/n-t` 没有参数也照样取得到。
 * 唯一认不准的是 3.3.4 整页加载 / 后退到一条只转义一次、桌名长得像转义的地址（字面的 `A%20B` 写成 `A%2520B`）—— 3.3.4 的路由参数同样读成 `A B`，
 * 与路由器一致（那是 3.3.4 自己读错，见 `tablePathSegment`）。
 * @param {String} path
 * @returns {String}
 */
function pathTableName(path) {
  if (!path.includes('/t/')) return ''
  const segment = path.split('/t/')[1].split(/[/?#]/)[0]
  try {
    return decodeURIComponent(segment)
  } catch (e) {
    return segment
  }
}
